/**
 * Auth context. Tracks the Firebase user and exposes sign-in/out. The server
 * is authoritative on whether a signed-in email is an allowed teacher; this
 * context only knows "is there a Firebase session". Pages discover the 403 case
 * from the API and render an access notice.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  firebaseConfigured,
  signInWithGoogle,
  signOut as fbSignOut,
  watchAuth,
  type FirebaseUser,
} from './firebase';

interface AuthState {
  user: FirebaseUser | null;
  /** Still resolving the initial auth state. */
  loading: boolean;
  configured: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState<boolean>(firebaseConfigured);

  useEffect(() => {
    if (!firebaseConfigured) {
      setLoading(false);
      return;
    }
    const unsub = watchAuth((u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      signIn: async () => {
        await signInWithGoogle();
      },
      signOut: async () => {
        await fbSignOut();
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
